// ADD these imports at the very top (line ~1)
// import { handleIncomingData, initializeFirmata } from './firmataManager.js';

// ============================================
// SERIAL CONNECTION VARIABLES
// ============================================
let serialPort = null;
let serialWriter = null;
let serialReader = null;

// ============================================
// BLUETOOTH CONNECTION VARIABLES
// ============================================
let bluetoothDevice = null;
let bluetoothCharacteristic = null;
let bluetoothServer = null;

// ============================================
// WIFI CONNECTION VARIABLES
// ============================================
let esp32IP = null;
let isWiFiConnected = false;

// ADD this line after it:
// let firmataEnabled = false; // ✅ DISABLED - Using Blockzie text protocol
let serialReadLoop = null;


// Bluetooth UUIDs
const BLUETOOTH_SERVICE_UUID = "d804b643-6ce7-4e81-9f8a-ce0f699085eb"
const BLUETOOTH_CHAR_UUID = "c8659211-af91-4ad3-a995-a58d6fd26145"

// ============================================
// GLOBAL STATE MANAGEMENT
// ============================================
function exposeGlobals() {
  if (typeof window !== 'undefined') {
    window.serialPort = serialPort;
    window.serialWriter = serialWriter;
    window.serialReader = serialReader;
    window.isSerialConnected = !!serialWriter;
    window.isBluetoothConnected = !!bluetoothCharacteristic;
    window.bluetoothDevice = bluetoothDevice;
    window.bluetoothCharacteristic = bluetoothCharacteristic;
    window.esp32IP = esp32IP;
    window.isWiFiConnected = isWiFiConnected;
  }
}

// ============================================
// SERIAL (USB) FUNCTIONS - YOUR ORIGINAL CODE
// ============================================
export async function connectSerial() {
  try {
    if (!("serial" in navigator)) {
      throw new Error("Web Serial API not supported. Use Chrome or Edge.");
    }

    // Request port with USB filter (exclude Bluetooth devices)
    const port = await navigator.serial.requestPort({
      filters: [
        // ESP32 with CP2102 USB chip (your device!)
        { usbVendorId: 0x10C4, usbProductId: 0xEA60 },

        // ESP32 with CH340 USB chip (alternative)
        { usbVendorId: 0x1A86, usbProductId: 0x7523 },

        // ESP32 with FTDI USB chip
        { usbVendorId: 0x0403, usbProductId: 0x6001 },

        // Arduino Uno
        { usbVendorId: 0x2341, usbProductId: 0x0043 },

        // Arduino Mega
        { usbVendorId: 0x2341, usbProductId: 0x0010 },

        // Generic USB-to-Serial adapters
        { usbVendorId: 0x067B, usbProductId: 0x2303 }, // Prolific
      ]
    });
    await port.open({
      baudRate: 57600,
      dataBits: 8,
      stopBits: 1,
      parity: "none",
      flowControl: "none"
    });


    // 🔴 CRITICAL: prevent ESP32 reset
    await port.setSignals({
      dataTerminalReady: false,
      requestToSend: false
    });

    console.log("✅ Serial port opened");

    // TEXT mode for Blockzie firmware
    serialWriter = port.writable.getWriter();
    serialReader = port.readable.getReader();
    serialPort = port;

    // Start reading incoming data
    startSerialReadLoop();

    // ✅ Wait for ESP32 firmware to boot completely
    console.log("⏳ Waiting for ESP32 firmware to boot...");
    await waitForFirmwareReady();

    /* =========================================================
       🔴 SERIAL WARM-UP (CRITICAL — matches blockzie.info)
       ========================================================= */
    console.log("🔥 Warming up firmware command parser...");

    const encoder = new TextEncoder();

    // Send harmless probe commands to wake command loop
    for (let i = 0; i < 3; i++) {
      try {
        await serialWriter.write(encoder.encode("PING\r\n"));
        await new Promise(r => setTimeout(r, 150));
      } catch (e) {
        console.warn("Warm-up write failed (ignored)", e);
      }
    }

    // Final stabilization delay
    await new Promise(r => setTimeout(r, 700));

    console.log("✅ Serial connected - firmware command loop ready");


    exposeGlobals();
    return { success: true };


  } catch (error) {
    console.error("❌ Serial connection failed:", error);
    throw error;
  }
}

async function startSerialReadLoop() {
  console.log("📖 Starting serial read loop...");

  try {
    while (serialReader && serialPort) {
      // Check if still connected before reading
      if (!serialReader || !serialPort) {
        console.log("📭 Read loop: Connection closed, exiting...");
        break;
      }

      try {
        const { value, done } = await serialReader.read();

        if (done) {
          console.log("📭 Serial stream closed");
          break;
        }

        if (value && value.length > 0) {
          console.log(`📥 Received ${value.length} bytes:`, Array.from(value));

          // ✅ Decode text response from Blockzie firmware
          try {
            const decoder = new TextDecoder();
            const textResponse = decoder.decode(value);
            console.log(`📥 Serial response: "${textResponse}"`);

            // Store response for debugging (optional)
            if (typeof window !== 'undefined') {
              window.lastSerialResponse = textResponse;
            }

            if (typeof window !== "undefined" && typeof window.onSerialData === "function") {
              window.onSerialData(value);
            }
          } catch (e) {
            console.warn('Could not decode serial response:', e);
          }
        }

      } catch (readError) {
        // If reader is cancelled, exit gracefully
        if (readError.name === 'NetworkError' || readError.message.includes('cancel')) {
          console.log("📭 Read cancelled, exiting loop");
          break;
        }
        console.error("❌ Read error:", readError);
        break;
      }
    }
  } catch (error) {
    console.error("❌ Serial read loop error:", error);
  } finally {
    console.log("🛑 Serial read loop stopped");
  }
}
/**
 * Wait for Blockzie firmware to finish booting
 */
async function waitForFirmwareReady() {
  return new Promise((resolve) => {
    let bootCompleteTimer = null;
    let lastMessageTime = Date.now();

    // Listen for incoming data
    const checkBootComplete = () => {
      const timeSinceLastMessage = Date.now() - lastMessageTime;

      // If no messages for 2 seconds, consider boot complete
      if (timeSinceLastMessage > 2000) {
        console.log('✅ No boot messages for 2s - firmware ready');
        clearInterval(bootCompleteTimer);
        resolve();
      }
    };

    // Store original handler
    const originalHandler = window.onSerialData;

    // Temporary handler to detect boot completion
    window.onSerialData = (data) => {
      lastMessageTime = Date.now();

      // Check for Blockzie ready message (if your firmware sends one)
      const decoder = new TextDecoder();
      const text = decoder.decode(data);

      if (text.includes('Blockzie') ||
        text.includes('Ready') ||
        text.includes('WiFi connected')) {
        console.log('✅ Blockzie firmware ready message detected');
        clearInterval(bootCompleteTimer);
        window.onSerialData = originalHandler;
        resolve();
      }
    };

    // Check every 500ms
    bootCompleteTimer = setInterval(checkBootComplete, 500);

    // Maximum wait time: 10 seconds
    setTimeout(() => {
      console.log('⚠️ Boot wait timeout - proceeding anyway');
      clearInterval(bootCompleteTimer);
      window.onSerialData = originalHandler;
      resolve();
    }, 10000);
  });
}

/**
 * Convert commands to Blockzie Serial format (Arduino-style)
 */
// function convertToBlockzieSerialFormat(command) {
//   console.log('🔄 Converting command:', command);

//   // SET_PIN_MODE 2 OUTPUT → pinMode(2, OUTPUT)
//   if (command.includes('SET_PIN_MODE')) {
//     const match = command.match(/SET_PIN_MODE (\d+) (\w+)/);
//     if (match) {
//       const pin = match[1];
//       const mode = match[2]; // OUTPUT, INPUT, PWM
//       return `pinMode(${pin},${mode})`;
//     }
//   }

//   // SET_DIGITAL_OUTPUT 2 HIGH → digitalWrite(2,HIGH)
//   if (command.includes('SET_DIGITAL_OUTPUT')) {
//     const match = command.match(/SET_DIGITAL_OUTPUT (\d+) (\w+)/);
//     if (match) {
//       const pin = match[1];
//       const value = match[2]; // HIGH or LOW
//       return `digitalWrite(${pin},${value})`;
//     }
//   }

//   // SET_ANALOG_OUTPUT (PWM) → analogWrite(pin, value)
//   if (command.includes('SET_ANALOG_OUTPUT')) {
//     const match = command.match(/SET_ANALOG_OUTPUT (\d+) (\d+)/);
//     if (match) {
//       const pin = match[1];
//       const value = match[2];
//       return `analogWrite(${pin},${value})`;
//     }
//   }

//   // If no match, return original
//   console.warn('⚠️ No conversion rule for:', command);
//   return command;
// }


export async function disconnectSerial() {
  console.log("🔌 Force disconnecting serial port...");

  const portToClose = serialPort;
  const writerToClose = serialWriter;
  const readerToClose = serialReader;

  // ⚡ CRITICAL: Stop the read loop FIRST
  // Set all to null immediately to stop the loop
  serialWriter = null;
  serialReader = null;
  serialPort = null;
  exposeGlobals();

  // Wait a moment for the read loop to detect the null and exit
  await new Promise(r => setTimeout(r, 100));

  // Now safely close everything
  try {
    // Cancel and release reader
    if (readerToClose) {
      try {
        await readerToClose.cancel();
        console.log("✅ Reader cancelled");
      } catch (e) {
        console.warn("Reader cancel error (OK):", e.message);
      }
    }

    // Release writer lock
    if (writerToClose) {
      try {
        await writerToClose.releaseLock();
        console.log("✅ Writer released");
      } catch (e) {
        console.warn("Writer release error (OK):", e.message);
      }
    }

    // Wait for streams to fully release
    await new Promise(r => setTimeout(r, 300));

    // Close the port
    if (portToClose && portToClose.readable) {
      try {
        await portToClose.close();
        console.log("✅ Port closed");
      } catch (e) {
        console.warn("Port close error (OK):", e.message);
      }
    }

    console.log("✅ Disconnect complete");
    return { success: true };

  } catch (error) {
    console.error("❌ Disconnect error:", error);
    // Force clear everything anyway
    serialPort = null;
    serialWriter = null;
    serialReader = null;
    exposeGlobals();
    throw error;
  }
}

// ============================================
// BLUETOOTH FUNCTIONS - NEW!
// ============================================

export async function connectBluetooth() {
  try {
    console.log('🔵 Requesting Bluetooth device...');

    if (!navigator.bluetooth) {
      throw new Error("Web Bluetooth API not supported");
    }

    // Request Bluetooth device with your UUIDs
    bluetoothDevice = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [BLUETOOTH_SERVICE_UUID]  // Keep this for later access
    });

    if (!bluetoothDevice) {
      throw new Error('No device selected');
    }

    console.log('✅ Device selected:', bluetoothDevice.name);
    console.log('🔵 Connecting to GATT Server...');

    // Connect to GATT server
    bluetoothServer = await bluetoothDevice.gatt.connect();
    console.log('✅ GATT Server connected');

    // Get service
    const service = await bluetoothServer.getPrimaryService(BLUETOOTH_SERVICE_UUID);
    console.log('✅ Service found');

    // Get characteristic
    bluetoothCharacteristic = await service.getCharacteristic(BLUETOOTH_CHAR_UUID);
    console.log('✅ Characteristic found');

    // Enable notifications to receive Blockzie text responses
    await bluetoothCharacteristic.startNotifications();
    bluetoothCharacteristic.addEventListener('characteristicvaluechanged', handleBluetoothData);
    console.log('✅ Notifications enabled');

    // Update state
    exposeGlobals();

    // Skip Firmata for Bluetooth (Blockzie firmware uses custom protocol)
    console.log('✅ Bluetooth uses custom protocol - skipping Firmata init');
    console.log('🎉 Bluetooth connected successfully!');

    return { success: true, device: bluetoothDevice.name };

  } catch (error) {
    console.error('❌ Bluetooth connection failed:', error);

    // Clean up on error
    bluetoothDevice = null;
    bluetoothCharacteristic = null;
    bluetoothServer = null;
    exposeGlobals();

    throw error;
  }
}

/**
 * Handle incoming Firmata data from Bluetooth
 */
function handleBluetoothData(event) {
  const value = new Uint8Array(event.target.value.buffer);
  console.log(`📥 Bluetooth received ${value.length} bytes:`, Array.from(value));

  // Blockzie firmware sends text responses, decode them
  const decoder = new TextDecoder();
  const textResponse = decoder.decode(value);
  console.log(`📥 Bluetooth response: "${textResponse}"`);

  // Store response for reading (optional)
  if (window.lastBluetoothResponse !== undefined) {
    window.lastBluetoothResponse = textResponse;
  }
}

// ✨ Helper function to blink LED
async function blinkLED(times) {
  if (!bluetoothCharacteristic) {
    console.warn('⚠️ Cannot blink LED: not connected');
    return;
  }

  const encoder = new TextEncoder();

  for (let i = 0; i < times; i++) {
    // Turn LED ON
    const onCommand = 'SET_DIGITAL_OUTPUT 2 HIGH\n';
    await bluetoothCharacteristic.writeValue(encoder.encode(onCommand));
    await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms

    // Turn LED OFF
    const offCommand = 'SET_DIGITAL_OUTPUT 2 LOW\n';
    await bluetoothCharacteristic.writeValue(encoder.encode(offCommand));
    await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms
  }

  console.log(`✅ Blinked LED ${times} times`);
}

export async function disconnectBluetooth() {
  console.log('🔌 Disconnecting Bluetooth...');

  // Turn off all outputs BEFORE disconnecting
  if (bluetoothCharacteristic) {
    try {
      console.log('🔌 Turning off all outputs...');

      // Turn off pins (adjust pin numbers as needed)
      await sendCommand('SET_DIGITAL_OUTPUT 2 LOW');
      await sendCommand('TURN_OFF_ALL_OUTPUTS');

      // Small delay to ensure commands are sent
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log('✅ Outputs turned off');
    } catch (error) {
      console.warn('⚠️ Could not turn off outputs:', error);
    }
  }

  const charToClose = bluetoothCharacteristic;
  const serverToClose = bluetoothServer;

  // Clear variables
  bluetoothCharacteristic = null;
  bluetoothServer = null;
  bluetoothDevice = null;
  exposeGlobals();

  try {
    // Stop notifications
    if (charToClose) {
      try {
        await charToClose.stopNotifications();
        charToClose.removeEventListener('characteristicvaluechanged', handleBluetoothData);
        console.log('✅ Notifications stopped');
      } catch (e) {
        console.warn('Notification stop error (OK):', e.message);
      }
    }

    // Disconnect GATT
    if (serverToClose && serverToClose.connected) {
      serverToClose.disconnect();
      console.log('✅ GATT disconnected');
    }

    console.log('✅ Bluetooth disconnect complete');
    return { success: true };

  } catch (error) {
    console.error('❌ Bluetooth disconnect error:', error);
    throw error;
  }
}

// export async function sendCommandBluetooth(command) {
//   try {
//     if (!window.bluetoothCharacteristic) {
//       throw new Error('Bluetooth not connected');
//     }

//     console.log('🔵 [BLUETOOTH] Sending:', command);
//     const commandWithNewline = command + '\n';
//     const encoder = new TextEncoder();
//     const data = encoder.encode(commandWithNewline);
//     await window.bluetoothCharacteristic.writeValue(data);
//     console.log('✅ [BLUETOOTH] Command sent');
//     return { success: true, method: 'bluetooth' };

//   } catch (error) {
//     console.error('❌ [BLUETOOTH] Send failed:', error);
//     throw error;
//   }
// }

// ============================================
// WIFI FUNCTIONS - NEW!
// ============================================

export async function connectWiFi(ip) {
  try {
    // Ask IP if not provided
    if (!ip) {
      ip = prompt("Enter ESP32 IP:", localStorage.getItem("esp32IP") || "192.168.31.128");
      if (!ip) throw new Error("No IP entered");
      localStorage.setItem("esp32IP", ip.trim());
    }

    ip = ip.trim();

    // Quick reachability test
    const root = await fetch(`http://${ip}/`, { method: "GET" });
    if (!root.ok) throw new Error("Device reachable but not responding");

    // Test /control using POST
    const ping = await fetch(`http://${ip}/control`, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: "PING"
    });

    if (!ping.ok) throw new Error("POST /control rejected");

    // SUCCESS
    window.esp32IP = ip;
    window.isWiFiConnected = true;
    console.log("CONNECTED to ESP32 WiFi at", ip);
    return { success: true, ip };

  } catch (e) {
    console.error("WiFi CONNECT ERROR =>", e);
    window.isWiFiConnected = false;
    window.esp32IP = null;
    return { success: false, error: e.message };
  }
}

export async function disconnectWiFi() {
  try {
    if (window.esp32IP && window.isWiFiConnected) {
      await fetch(`http://${window.esp32IP}/disconnect`, { method: "POST" })
        .catch(() => console.warn("ESP disconnect unreachable"));
    }
  } catch (e) { console.warn("Disconnect error", e); }

  // ALWAYS CLEAR STATE
  window.isWiFiConnected = false;
  window.esp32IP = null;
  localStorage.removeItem("esp32IP");

  console.log("WiFi DISCONNECTED + STATE CLEARED");
  return { success: true };
}

async function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function blinkLEDWiFi(times) {
  if (!esp32IP) {
    console.warn('⚠️ Cannot blink LED: WiFi not connected');
    return;
  }

  for (let i = 0; i < times; i++) {
    try {
      // LED ON
      await fetch(`http://${esp32IP}/control`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: 'SET_DIGITAL_OUTPUT 2 HIGH\n',
        mode: 'no-cors'
      }).catch(() => { });
      await new Promise(resolve => setTimeout(resolve, 200));

      // LED OFF
      await fetch(`http://${esp32IP}/control`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: 'SET_DIGITAL_OUTPUT 2 LOW\n',
        mode: 'no-cors'
      }).catch(() => { });
      await new Promise(resolve => setTimeout(resolve, 200));
    } catch (error) {
      console.warn('⚠️ LED blink failed:', error);
    }
  }

  console.log(`✅ Blinked LED ${times} times via WiFi`);
}

// export async function disconnectWiFi() {
//   try {
//     console.log('📡 [STEP 1] Disconnecting WiFi...');

//     // Send LED OFF command ONLY (don't try /disconnect endpoint)
//     if (esp32IP && isWiFiConnected) {
//       try {
//         console.log('💡 Turning LED OFF...');
//         await fetch(`http://${esp32IP}/control`, {
//           method: 'POST',
//           headers: { 'Content-Type': 'text/plain' },
//           body: 'SET_DIGITAL_OUTPUT 2 LOW\n',
//           mode: 'no-cors'
//         }).catch(() => { });

//         // Wait for command to execute
//         await new Promise(resolve => setTimeout(resolve, 300));
//         console.log('✅ LED OFF command sent');
//       } catch (e) {
//         console.warn('⚠️ Could not send LED OFF:', e);
//       }
//     }

//     // Store previous IP for logging
//     const previousIP = esp32IP;

//     // Clear state
//     esp32IP = null;
//     isWiFiConnected = false;
//     localStorage.removeItem('esp32IP');
//     exposeGlobals();

//     console.log('✅ [STEP 2] WiFi state cleared');
//     console.log('   Previous IP:', previousIP);
//     console.log('   window.esp32IP:', window.esp32IP);
//     console.log('   window.isWiFiConnected:', window.isWiFiConnected);
//     console.log('🎉 WiFi disconnected successfully!');

//     return { success: true };

//   } catch (error) {
//     console.error('❌ WiFi disconnect failed:', error);

//     // Force clear state even on error
//     esp32IP = null;
//     isWiFiConnected = false;
//     localStorage.removeItem('esp32IP');
//     exposeGlobals();

//     throw error;
//   }
// }

// export async function sendCommandWiFi(command) {
//   try {
//     if (!window.esp32IP || !window.isWiFiConnected) {
//       throw new Error('WiFi not connected');
//     }

//     console.log('📡 [WIFI] Sending:', command);

//     await fetch(`http://${window.esp32IP}/control`, {
//       method: 'POST',
//       headers: { 'Content-Type': 'text/plain' },
//       body: command + '\n',
//       mode: 'no-cors'
//     }).catch(() => { });

//     console.log('✅ [WIFI] Command sent');
//     return { success: true, method: 'wifi' };

//   } catch (error) {
//     console.error('❌ [WIFI] Send failed:', error);
//     throw error;
//   }
// }


// ============================================
// UNIVERSAL SEND COMMAND
// ============================================

function convertToSerialCommand(command) {
  // SET_PIN_MODE 5 OUTPUT → pinMode(5,OUTPUT)
  if (command.startsWith("SET_PIN_MODE")) {
    const [, pin, mode] = command.split(" ");
    return `pinMode(${pin},${mode})`;
  }

  // SET_DIGITAL_OUTPUT 5 1 → digitalWrite(5,HIGH/LOW)
  if (command.startsWith("SET_DIGITAL_OUTPUT")) {
    const [, pin, value] = command.split(" ");
    const level = value === "1" ? "HIGH" : "LOW";
    return `digitalWrite(${pin},${level})`;
  }

  // fallback (send as-is)
  return command;
}

export async function sendCommand(command) {
  if (!command || typeof command !== "string") {
    throw new Error("Invalid command");
  }
  // 🔒 RECOVER ACTIVE CONNECTION IF LOST (DO NOT TOUCH SERIAL LOGIC)
  if (!window.activeConnection) {
    if (window.isBluetoothConnected && window.bluetoothCharacteristic) {
      window.activeConnection = "bluetooth";
    } else if (window.isSerialConnected && serialWriter) {
      window.activeConnection = "serial";
    } else if (window.isWiFiConnected && window.esp32IP) {
      window.activeConnection = "wifi";
    }
  }

  const payload = command.trim() + "\r\n";
  const encoder = new TextEncoder();

  // 1️⃣ SERIAL
  // 1️⃣ SERIAL (FIRMATA BINARY — REQUIRED)
  if (serialWriter && window.isSerialConnected) {

    // SET_PIN_MODE <pin> OUTPUT
    if (command.startsWith("SET_PIN_MODE")) {
      const [, pinStr, mode] = command.split(" ");
      const pin = parseInt(pinStr, 10);

      // Firmata: PIN_MODE (0xF4), pin, OUTPUT (0x01)
      await serialWriter.write(new Uint8Array([
        0xF4,
        pin,
        0x01
      ]));

      console.log("📡 [FIRMATA] PIN_MODE OUTPUT", pin);
      return { success: true, method: "serial" };
    }

    // SET_DIGITAL_OUTPUT <pin> <HIGH|LOW|1|0>
    if (command.startsWith("SET_DIGITAL_OUTPUT")) {
      const [, pinStr, valueStr] = command.split(" ");
      const pin = parseInt(pinStr, 10);
      const value = (valueStr === "HIGH" || valueStr === "1") ? 1 : 0;

      const port = Math.floor(pin / 8);
      const bit = pin % 8;
      const portValue = value << bit;

      // Firmata DIGITAL_MESSAGE (0x90)
      await serialWriter.write(new Uint8Array([
        0x90 | port,
        portValue & 0x7F,
        (portValue >> 7) & 0x7F
      ]));

      console.log("📡 [FIRMATA] DIGITAL_WRITE", pin, value);
      return { success: true, method: "serial" };
    }

    console.warn("⚠️ Unsupported Serial command:", command);
  }



  // 2️⃣ BLUETOOTH
  if (
    window.activeConnection === "bluetooth" &&
    window.bluetoothCharacteristic &&
    window.isBluetoothConnected
  ) {
    let bleCommand = command.trim();

    if (bleCommand.startsWith("SET_DIGITAL_OUTPUT")) {
      const [_, pin, value] = bleCommand.split(" ");
      const normalizedValue = (value === "1" || value === "HIGH") ? "HIGH" : "LOW";
      bleCommand = `SET_DIGITAL_OUTPUT ${pin} ${normalizedValue}`;
    }

    const blePayload = bleCommand + "\n";

    console.log("📡 [BLUETOOTH]", blePayload);
    await window.bluetoothCharacteristic.writeValue(
      new TextEncoder().encode(blePayload)
    );
    return { success: true, method: "bluetooth" };
  }


  // 3️⃣ WIFI
  if (
    window.activeConnection === "wifi" &&
    window.esp32IP &&
    window.isWiFiConnected
  ) {
    let wifiCommand = command.trim();

    // 🔵 WiFi firmware expects HIGH / LOW
    if (wifiCommand.startsWith("SET_DIGITAL_OUTPUT")) {
      const [_, pin, value] = wifiCommand.split(" ");
      const normalizedValue =
        value === "1" || value === "HIGH" ? "HIGH" : "LOW";
      wifiCommand = `SET_DIGITAL_OUTPUT ${pin} ${normalizedValue}`;
    }

    const wifiPayload = wifiCommand + "\n";

    console.log("📡 [WIFI]", wifiPayload);

    await fetch(`http://${window.esp32IP}/control`, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: wifiPayload
    });

    return { success: true, method: "wifi" };
  }

  throw new Error("No active connection");
}
// 👇 ADD THIS EXACTLY HERE (AFTER sendCommand)
if (typeof window !== "undefined") {
  window.sendCommand = sendCommand;
}


// ============================================
// CONNECTION STATUS
// ============================================

// ============================================
// WIFI SEND — Convert bytes → Text command
// ============================================
// export async function sendFirmataBytes(bytes) {
//   // Convert Firmata bytes to text command
//   const cmd = convertFirmataToBlockzieCommand(bytes);
//   if (!cmd) throw new Error("Unsupported command");

//   console.log("📤 Blockzie command:", cmd);

//   // Try Serial first
//   if (serialWriter && window.isSerialConnected) {
//     // ✅ Convert to Arduino format for Serial
//     const arduinoCmd = convertToBlockzieSerialFormat(cmd);
//     console.log("📤 Arduino format:", arduinoCmd);

//     const encoder = new TextEncoder();
//     await serialWriter.write(encoder.encode(arduinoCmd + "\n"));
//     console.log("✅ Sent via Serial");
//     return true;
//   }

//   // Try Bluetooth
//   if (window.bluetoothCharacteristic && window.isBluetoothConnected) {
//     const encoder = new TextEncoder();
//     await window.bluetoothCharacteristic.writeValue(encoder.encode(cmd + "\n"));
//     console.log("✅ Sent via Bluetooth");
//     return true;
//   }

//   // Try WiFi (uses original text format)
//   if (window.esp32IP && window.isWiFiConnected) {
//     await fetch(`http://${window.esp32IP}/control`, {
//       method: "POST",
//       headers: { "Content-Type": "text/plain" },
//       body: cmd
//     });
//     console.log("✅ Sent via WiFi");
//     return true;
//   }

//   throw new Error("No active connection");
// }


/**
 * Send text command to Bluetooth (for Blockzie firmware)
 */
// async function sendBluetoothTextCommand(command) {
//   if (!bluetoothCharacteristic) {
//     throw new Error('Bluetooth not connected');
//   }

//   console.log(`📤 Bluetooth text command: "${command}"`);

//   const encoder = new TextEncoder();
//   const bytes = encoder.encode(command + '\n');

//   await bluetoothCharacteristic.writeValue(bytes);
//   return true;
// }

/**
 * Convert Firmata bytes to Blockzie text command
 */
// function convertFirmataToBlockzieCommand(bytes) {
//   // SET_PIN_MODE command (0xF4)
//   if (bytes[0] === 0xF4) {
//     const pin = bytes[1];
//     const mode = bytes[2] === 1 ? 'OUTPUT' : bytes[2] === 0 ? 'INPUT' : 'PWM';
//     return `SET_PIN_MODE ${pin} ${mode}`;
//   }

//   // DIGITAL_WRITE command (0xF5)
//   if (bytes[0] === 0xF5) {
//     const pin = bytes[1];
//     const value = bytes[2] === 1 ? 'HIGH' : 'LOW';
//     return `SET_DIGITAL_OUTPUT ${pin} ${value}`;
//   }

//   // ANALOG_MESSAGE (PWM) - command range 0xE0-0xEF
//   if ((bytes[0] & 0xF0) === 0xE0) {
//     const pin = bytes[0] & 0x0F;
//     const value = bytes[1] | (bytes[2] << 7);
//     return `SET_ANALOG_OUTPUT ${pin} ${value}`;
//   }

//   console.warn('⚠️ Unknown Firmata command, cannot convert:', Array.from(bytes));
//   return null;
// }

/**
 * Send command to ESP32 via WiFi
 */
// async function sendWiFiCommand(command, retries = 2) {
//   if (!esp32IP) {
//     throw new Error('WiFi not connected - no IP address');
//   }

//   console.log(`📤 WiFi command to ${esp32IP}: "${command}"`);

//   for (let attempt = 1; attempt <= retries; attempt++) {
//     try {
//       const url = `http://${esp32IP}/control`;
//       console.log(`📡 Attempt ${attempt}/${retries} - Sending POST to: ${url}`);
//       console.log(`📦 Body: ${command}`);

//       const response = await fetch(url, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'text/plain',
//           'Accept': '*/*',
//         },
//         body: command,
//         signal: AbortSignal.timeout(10000)  // Increased to 10 seconds
//       });

//       if (response.ok) {
//         const result = await response.text();
//         console.log(`✅ WiFi response: "${result}"`);
//         return result;
//       } else {
//         const errorText = await response.text();
//         throw new Error(`HTTP ${response.status}: ${errorText}`);
//       }

//     } catch (error) {
//       if (attempt < retries) {
//         console.warn(`⚠️ Attempt ${attempt} failed, retrying...`);
//         await new Promise(resolve => setTimeout(resolve, 1000));  // Wait 1 second before retry
//         continue;
//       }

//       console.error('❌ WiFi command failed after all retries:', error);
//       throw error;
//     }
//   }
// }

export function isConnected() {
  return !!serialWriter || !!window.bluetoothCharacteristic || window.isWiFiConnected;
}

export function getConnectionStatus() {
  return {
    serial: !!serialWriter,
    bluetooth: !!window.bluetoothCharacteristic,
    wifi: window.isWiFiConnected,
    active: serialWriter ? 'serial' : (window.bluetoothCharacteristic ? 'bluetooth' : (window.isWiFiConnected ? 'wifi' : null))
  };
}
